import { Observable } from 'rxjs/Observable'
import 'rxjs/add/observable/forkJoin'
import { push } from 'react-router-redux'
import { combineEpics } from 'redux-observable'
import {setAccessToken} from '@drifthq/web-auth'
import { filter } from 'lodash'
import isMobile from 'ismobilejs'
import Cookies from 'js-cookie'
import queryString from 'query-string'

import { isExtensionInstalled } from 'utils/extension'
import config from 'config'
import * as actions from './actions'
import * as selectors from './selectors'
import * as UserActions from 'modules/user/actions'
import * as UserSelectors from 'modules/user/selectors'
import * as RoutingSelectors from 'modules/routing/selectors'
import * as BillingSelectors from 'modules/billing/selectors'
import { identifyUser, track } from 'utils/trackingUtils'
import { api } from './api'
import { api as UserApi } from 'modules/user/api'
import { Flow } from 'constants/flowConstants'

const HOME_REDIRECT_URL = `${config.CUSTOMER_WEB_URL}/home`
const MARKETO_MUNCHKIN_COOKIE = '_mkto_trk'

const createOrg = action$ => {
  return action$
    .ofType(actions.createOrg.toString())
    .pluck('payload')
    .mergeMap(({ email, password, anonymousId, configurationOptions, enableDefaultWelcomeMessages }) => {
      return api
        .create({
          name: email,
          email,
          password,
          configuration: { organizationName: email, ...configurationOptions },
          gates: {
            chat: true,
          },
          properties: {
            originalReferrer: document.referrer,
            anonymousId,
          },
          },
          enableDefaultWelcomeMessages
        )
        .map(response => {
          const token = response.accessToken.accessToken
          setAccessToken({ token: token })
          identifyUser(undefined, response.organization.id, response.user.id)
          return actions.createOrgSuccess({ response })
        })
        .startWith(actions.createOrgPending())
        .catch((error) => Observable.of(actions.createOrgFailed(), actions.setCreateOrgError({error})))
    })
}

const updateOrg = (action$, store) => {
  return action$
    .ofType(actions.updateOrg.toString())
    .pluck('payload')
    .mergeMap((configuration) => {
      const orgId = selectors.getOrgId(store.getState())
      if (!orgId) {
        return Observable.of(actions.updateOrgFailed())
      }

      return api.updateOrg(orgId, configuration)
        .map((res) => {
          return actions.updateOrgSuccess(res)
        })
        .startWith(actions.updateOrgPending())
        .catch(() => Observable.of(actions.updateOrgFailed()))
    })
}

const signup = (action$, store) => {
  return action$
    .ofType(actions.signup.toString())
    .pluck('payload')
    .delay(1000)
    .mergeMap(({ email, route }) => {
      window.ga && window.ga('send', 'event', 'PQL', 'PQL Email Captured')
      return api
        .trackSignup({
          email: email,
          route: route ? 'https://www.drift.com' + route : document.referrer,
          mobile: isMobile.any,
          marketoMunchkinCookie: Cookies.get(MARKETO_MUNCHKIN_COOKIE),
          anonymousId: (window.analytics && window.analytics.user) ? window.analytics.user().anonymousId() : undefined,
        })
        .map(actions.signupSuccess)
        .startWith(actions.signupPending())
        .catch(() => Observable.of(actions.signupFailed()))
    })
}


const chatProductEnabled = (action$, store) => {
  return action$
    .ofType(actions.createOrgSuccess.toString(), UserActions.fetchUserSuccess.toString())
    .map(() => track('Product Enabled', { product: 'chat' }))
    .ignoreElements()
}

const trackOrgCreateSignup = (action$, store) => {
  return action$
    .ofType(actions.createOrgSuccess.toString())
    .pluck('payload')
    .filter(() => UserSelectors.getDisabledEmail(store.getState()))
    .map(({ response }) => actions.signup({ email: response.user.email, route: undefined }))
}

const widgetOnboardingRedirect = (action$, store) => {
  return action$
    .ofType(actions.createOrgSuccess.toString())
    .pluck('payload')
    .filter(() => UserSelectors.getFlow(store.getState()) === Flow.WIDGET)
    .map(({ response }) => {
      const state = store.getState()
      const plan = BillingSelectors.getPlan(state)
      const coupon = BillingSelectors.getCoupon(state)

      if (plan) {
        const queryParams = { plan: plan, coupon: coupon }
        return push(`/billing?${queryString.stringify(queryParams)}`)
      } else {
        track('Onboarding Account Setup Completed', {
          context: 'widget-onboarding',
          mobile: isMobile.any,
        })
        if (isMobile.any) {
          return push('/setup/touchless/success')
        } else {
          return push('/setup/touchless/custom')
        }
      }
    })
}

const videoOnboardingRedirect = (action$, store) => {
  return action$
    .ofType(actions.createOrgSuccess.toString())
    .pluck('payload')
    .filter(() => UserSelectors.getFlow(store.getState()) === Flow.VIDEO)
    .mergeMap(() => {
      return Observable.create(observer => {
        if (isMobile.any) {
          observer.next(push('/video/mobile'))
        } else {
          isExtensionInstalled()
            .then(installed => {
              if (installed) {
                observer.next(push('/video/landing'))
              } else {
                observer.next(push('/video/extension'))
              }
            })
        }
      })
    })
}

const updateOrgProperties = (action$, store) => {
  return action$
    .ofType(actions.updateOrgProperties.toString())
    .pluck('payload')
    .mergeMap(({ properties, redirect, redirectUrl }) => {
      const orgId = selectors.getOrgId(store.getState())
      return api
        .updateProperties(orgId, properties)
        .map(() => {
          if (redirect) {
            const redirectTo = redirectUrl || HOME_REDIRECT_URL
            window.location.replace(redirectTo)
          }
          return actions.updateOrgPropertiesSuccess()
        })
        .startWith(actions.updateOrgPropertiesPending())
        .catch(() => Observable.of(actions.updateOrgPropertiesFailed()))
    })
}

const fetchEmbed = action$ => {
  return action$
    .ofType(actions.fetchEmbed.toString())
    .pluck('payload')
    .mergeMap(operations => {
      return api
        .rxFetchEmbed(operations)
        .map(actions.fetchEmbedSuccess)
        .startWith(actions.fetchEmbedPending())
        .catch(() => Observable.of(actions.fetchEmbedFailed()))
  })
}

const updateEmbed = action$ => {
  return action$
    .ofType(actions.updateEmbed.toString())
    .pluck('payload')
    .mergeMap(operations => {
      return api
        .updateEmbed(operations)
        .map(actions.updateEmbedSuccess)
        .startWith(actions.updateEmbedPending())
        .catch(() => Observable.of(actions.updateEmbedFailed()))
    })
}

const updateWelcomeMessage = action$ => {
  return action$
    .ofType(actions.updateWelcomeMessage.toString())
    .pluck('payload')
    .mergeMap(({ onlineText, offlineText }) => {
      return api
        .updateEmbed([{
          operation: 'set',
          value: onlineText,
          property: 'theme.welcomeMessage'
        }])
        .map(() => actions.updateWelcomeMessageSuccess({ onlineText, offlineText }))
        .startWith(actions.updateWelcomeMessagePending())
        .catch(() => Observable.of(actions.updateWelcomeMessageFailed()))
    })
}

const checkUniqueHandle = action$ => {
  return action$
    .ofType(actions.checkUniqueHandle.toString())
    .debounceTime(200)
    .pluck('payload')
    .mergeMap(handle => {
      return api
        .checkUniqueHandle(handle)
        .map(actions.checkUniqueHandleFailed)
        .catch(error => {
          if (error.status === 404) {
            return Observable.of(actions.checkUniqueHandleSuccess())
          }
          return Observable.of(actions.checkUniqueHandleFailed())
        })
    })
}

const fetchOrg = action$ => {
  return action$
    .ofType(UserActions.fetchUserSuccess.toString())
    .pluck('payload')
    .mergeMap(res => {
      return api
        .fetch(res.orgId)
        .map(actions.fetchOrgSuccess)
        .startWith(actions.fetchOrgPending())
        .catch(() => Observable.of(actions.fetchOrgFailed()))
    })
}

const fetchOrgProperties = (action$, store) => {
  return action$
    .ofType(actions.fetchOrgSuccess.toString())
    .pluck('payload')
    .mergeMap(() => {
      const orgId = selectors.getOrgId(store.getState())
      return Observable.fromPromise(api
        .fetchOrgProperties(orgId))
        .map(actions.fetchOrgPropertiesSuccess)
        .catch(() => Observable.of(actions.fetchOrgPropertiesFailed()))
    })
}

const fetchUsers = action$ => {
  return action$
    .ofType(UserActions.fetchUserSuccess.toString())
    .pluck('payload')
    .mergeMap(res => {
      const userId = res.id
      return api
        .fetchUsers(res.orgId)
        .map((res) => {
          const usersWithoutCurrentUser = filter(res, (user) => user.id !== userId)
          return actions.fetchOrgUsersSuccess(usersWithoutCurrentUser)
        })
        .startWith(actions.fetchOrgUsersPending())
        .catch(() => Observable.of(actions.fetchOrgUsersFailed()))
    })
}

const exchangeGoogleCodeForDriftCode = action$ => {
  return action$
    .ofType(actions.exchangeGoogleCodeForDriftCode.toString())
    .pluck('payload')
    .mergeMap(res => {
      return api
        .exchangeGoogleCodeForDriftCode(res)
        .map(response => actions.exchangeThirdPartyCodeSuccess(response))
        .catch(() =>  Observable.of(actions.setOAuthError("Sorry, we ran into an error! Try again")))
    })
}

const exchangeMicrosoftCodeForDriftCode = action$ => {
  return action$
    .ofType(actions.exchangeMicrosoftCodeForDriftCode.toString())
    .pluck('payload')
    .mergeMap(res => {
      return api
        .exchangeMicrosoftCodeForDriftCode(res)
        .map(response => actions.exchangeThirdPartyCodeSuccess(response))
        .catch(() =>  Observable.of(actions.setOAuthError("Sorry, we ran into an error! Try again")))
    })
}

const exchangeThirdPartyCodeSuccess = (action$, store) => {
  return action$
    .ofType(actions.exchangeThirdPartyCodeSuccess.toString())
    .pluck('payload')
    .mergeMap((response) => {
      const state = store.getState()
      const pathname = RoutingSelectors.getPathname(state)
      let route = ""
      if (pathname.includes("google")) {
        route = pathname.replace("/start/google", "")
      } else if (pathname.includes("microsoft")) {
        route = pathname.replace("/start/microsoft", "")
      }
      if (response.message === 'Third-Party Sign Up') {
        const token = response.value
        setAccessToken({ token: token })
        return Observable.forkJoin(UserApi.fetchUser())
          .switchMap(( value ) => Observable.of(actions.thirdPartyOnboardingRedirect(response), actions.signup({ email: value.pop().email, route: route })))
      } else if (response.message === 'Third-Party Login') {
        return Observable.of(actions.thirdPartyOnboardingSignIn(response))
      }
      return Observable.empty()
    })
}

const thirdPartyOnboardingSignIn = action$ => {
  return action$
    .ofType(actions.thirdPartyOnboardingSignIn.toString())
    .pluck('payload')
    .map((response) => {
      const token = response.value
      const nextUrl = `${config.CUSTOMER_WEB_URL}?code=${token}`
      return window.location.replace(nextUrl)
    })
}

const thirdPartyOnboardingRedirect = (action$, store) => {
  return action$
    .ofType(actions.thirdPartyOnboardingRedirect.toString())
    .pluck('payload')
    .map(() => {
      let path = ''
      if (!!BillingSelectors.getPlan(store.getState())) {
        path = '/billing'
      } else if (isMobile.any) {
        path = '/setup/touchless/mobile'
      } else {
        path = '/setup/touchless/custom'
      }
      return push(path)
    })
}

export const epic = combineEpics(
  createOrg,
  fetchOrg,
  fetchOrgProperties,
  widgetOnboardingRedirect,
  videoOnboardingRedirect,
  updateOrgProperties,
  trackOrgCreateSignup,
  fetchEmbed,
  updateEmbed,
  updateWelcomeMessage,
  checkUniqueHandle,
  fetchUsers,
  updateOrg,
  exchangeGoogleCodeForDriftCode,
  exchangeMicrosoftCodeForDriftCode,
  exchangeThirdPartyCodeSuccess,
  thirdPartyOnboardingSignIn,
  thirdPartyOnboardingRedirect,
  signup,
  chatProductEnabled
)



// WEBPACK FOOTER //
// ./src/modules/org/epic.js